import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Lock, ArrowLeft, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand name
const BRAND_NAME = "Stone Sills Direct";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin && formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const endpoint = isLogin ? "/admin/login" : "/admin/register";
      const response = await axios.post(`${API}${endpoint}`, {
        username: formData.username,
        password: formData.password,
      });

      localStorage.setItem("adminToken", response.data.token);
      toast.success(isLogin ? "Welcome back!" : "Admin account created!");
      navigate("/admin");
    } catch (error) {
      const message = error.response?.data?.detail || "Authentication failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center p-6">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link
          to="/"
          className="flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0F3D2E] mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="stone-card" data-testid="admin-login-card">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#0F3D2E] flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium">
                {isLogin ? "Admin Login" : "Create Admin"}
              </h1>
              <p className="text-sm text-[#57534E]">
                {isLogin ? "Access the dashboard" : "Set up your admin account"}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="input-label">Username</Label>
              <Input
                required
                value={formData.username}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, username: e.target.value }))
                }
                className="input-field"
                placeholder="Enter username"
                data-testid="admin-username"
              />
            </div>

            <div>
              <Label className="input-label">Password</Label>
              <Input
                type="password"
                required
                value={formData.password}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, password: e.target.value }))
                }
                className="input-field"
                placeholder="Enter password"
                data-testid="admin-password"
              />
            </div>

            {!isLogin && (
              <div>
                <Label className="input-label">Confirm Password</Label>
                <Input
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      confirmPassword: e.target.value,
                    }))
                  }
                  className="input-field"
                  placeholder="Confirm password"
                  data-testid="admin-confirm-password"
                />
              </div>
            )}

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="admin-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isLogin ? "Signing in..." : "Creating account..."}
                </>
              ) : isLogin ? (
                "Sign In"
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-[#57534E] hover:text-[#0F3D2E]"
              data-testid="toggle-auth-mode"
            >
              {isLogin
                ? "Need to create an admin account?"
                : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
